import { useCallback } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { inferRouterOutputs } from "@trpc/server";

import { trpc, AppRouterType } from "@/common/helpers/trpc";

type CheckDescriptionGenerationOutput = inferRouterOutputs<AppRouterType>["getFormGeneratedByAIData"];

export type DescriptionGenerationStatus = NonNullable<CheckDescriptionGenerationOutput>["descriptionStatus"];

export interface PollDescriptionGenerationResult {
  descriptionStatus: DescriptionGenerationStatus;
  description: string | null;
}

/**
 * Polls the backend until the description generation
 * either succeeds or fails – or until a timeout is reached.
 */
export const useDescriptionGenerationPolling = (formId: string) => {
  const { mutateAsync: getFormGeneratedByAIData } = trpc.getFormGeneratedByAIData.useMutation();

  const pollDescriptionGeneration = useCallback(
    async ({
      pollIntervalMs = 800,
      timeoutMs = 25_000,
      minimumAnimationMs = 1500,
    }: {
      pollIntervalMs?: number;
      timeoutMs?: number;
      minimumAnimationMs?: number;
    } = {}): Promise<PollDescriptionGenerationResult> => {
      const startTime = Date.now();

      const wait = async (ms: number) => {
        if (ms > 0) {
          await new Promise((resolve) => setTimeout(resolve, ms));
        }
      };

      while (Date.now() - startTime < timeoutMs) {
        const res = await getFormGeneratedByAIData({ formId });
        const status = res?.descriptionStatus ?? null;
        const description = res?.description ?? null;

        if (!status) {
          const elapsedMs = Date.now() - startTime;

          if (elapsedMs >= 1_500) {
            const remainingAnimationMs = Math.max(minimumAnimationMs - elapsedMs, 0);
            if (remainingAnimationMs > 0) {
              await wait(remainingAnimationMs);
            }

            const fallbackStatus = "failed" as DescriptionGenerationStatus;

            datadogLogs.logger.warn(
              `AI description polling failed because no status received within 1500ms for form ${formId}`,
              {
                formId,
                elapsedMs,
              }
            );

            return {
              descriptionStatus: fallbackStatus,
              description: null,
            };
          }

          const remainingAnimationMs = Math.max(minimumAnimationMs - elapsedMs, 0);
          const delayMs = remainingAnimationMs > 0 ? Math.min(pollIntervalMs, remainingAnimationMs) : pollIntervalMs;
          await wait(delayMs);
          continue;
        }

        if (status === "completed" || status === "failed") {
          // Ensure minimum animation time has passed
          const elapsedMs = Date.now() - startTime;
          if (elapsedMs < minimumAnimationMs) {
            await wait(minimumAnimationMs - elapsedMs);
          }

          const totalElapsedMs = Date.now() - startTime;
          datadogLogs.logger.info(`AI description polling completed in ${totalElapsedMs}ms with status ${status}`, {
            formId,
            descriptionStatus: status,
            elapsedMs: totalElapsedMs,
          });
          return { descriptionStatus: status, description };
        }

        await wait(pollIntervalMs);
      }

      const elapsedMs = Date.now() - startTime;
      const timeoutStatus = "failed" as DescriptionGenerationStatus;

      datadogLogs.logger.warn(`AI description polling timeout or failed after ${elapsedMs}ms`, {
        formId,
        descriptionStatus: timeoutStatus,
        elapsedMs,
      });
      return { descriptionStatus: timeoutStatus, description: null };
    },
    [getFormGeneratedByAIData, formId]
  );

  return { pollDescriptionGeneration };
};
